import {FC, useEffect, useRef, useState} from 'react'
import clsx from 'clsx'
import {getPaymentResult, PaymentResult, readApiError, rial} from '../core/onlinePaymentApi'
import './OnlinePayment.scss'

const POLL_MS = 2500
const POLL_LIMIT = 30

/** The ?payment= a gateway's return brought the payer back with, if any. */
export const readPaymentParam = () => {
  const value = new URLSearchParams(window.location.search).get('payment')
  return value && /^[0-9a-f]{32}$/i.test(value) ? value : undefined
}

/** Drops ?payment= from the address so a reload does not show the banner again. */
export const clearPaymentParam = () => {
  const url = new URL(window.location.href)
  if (!url.searchParams.has('payment')) return
  url.searchParams.delete('payment')
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
}

const settledOf = (result: PaymentResult) =>
  result.status !== 'pending' && result.status !== 'unknown' && !(result.status === 'paid' && (result.apply === 'pending' || result.apply === 'applying'))

/**
 * What became of the payment the payer just made: polled until the gateway's verification and the payment
 * row (or the wallet credit) are settled, then shown with its reference number. <paramref name="onSettled"/>
 * lets the page reload the invoice (a payment row creates a new version of an invoice).
 */
export const PaymentResultBanner: FC<{
  publicId: string
  onSettled?: (result: PaymentResult) => void
  onClose?: () => void
  /** Where an issued invoice opens from: the panel's address on another site (the goods/services ID search). */
  invoiceBase?: string
}> = ({publicId, onSettled, onClose, invoiceBase = ''}) => {
  const [result, setResult] = useState<PaymentResult>()
  const [error, setError] = useState('')
  const [attempts, setAttempts] = useState(0)
  const settledRef = useRef(false)
  // The page passes a fresh function on every render; the poll reads the latest one without restarting.
  const onSettledRef = useRef(onSettled)
  onSettledRef.current = onSettled

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const poll = async (count: number) => {
      try {
        const next = await getPaymentResult(publicId)
        if (cancelled) return
        setResult(next)
        setAttempts(count)
        if (settledOf(next)) {
          if (!settledRef.current) {
            settledRef.current = true
            onSettledRef.current?.(next)
          }
          return
        }
      } catch (pollError) {
        if (cancelled) return
        setError(readApiError(pollError, 'نتیجهٔ پرداخت دریافت نشد.').message)
      }

      if (count < POLL_LIMIT) {
        timer = setTimeout(() => poll(count + 1), POLL_MS)
      }
    }

    poll(1)
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [publicId])

  const close = () => {
    clearPaymentParam()
    onClose?.()
  }

  if (!result) {
    return (
      <div className={clsx('op-result', error ? 'op-result--error' : 'op-result--pending')} role='status' aria-live='polite'>
        {error ? (
          <span className='flex-grow-1'>{error}</span>
        ) : (
          <>
            <span className='spinner-border spinner-border-sm' aria-hidden='true' />
            <span className='flex-grow-1'>در حال دریافت نتیجهٔ پرداخت از درگاه…</span>
          </>
        )}
        <button type='button' className='btn btn-sm btn-link' onClick={close}>
          بستن
        </button>
      </div>
    )
  }

  const wallet = result.purpose === 'wallet'
  // A person's «دسته‌بندی شناسه کالا/خدمت» subscription (2026-10-18).
  const subscription = result.purpose === 'stuffid'
  let tone: 'success' | 'pending' | 'warning' | 'error'
  let title: string
  let detail: string | undefined
  if (result.status === 'paid') {
    if (result.apply === 'applied' || result.apply === 'notApplicable') {
      tone = 'success'
      title = subscription
        ? `پرداخت ${rial(result.amount)} ریال انجام شد و اشتراک دسته‌بندی شناسه کالا/خدمت فعال شد.`
        : wallet
        ? `کیف پول ${rial(result.walletCredit ?? result.amount)} ریال شارژ شد.`
        : `پرداخت ${rial(result.amount)} ریال انجام شد و در جدول پرداخت ${result.isProforma ? 'پیش‌فاکتور' : 'صورتحساب'} ثبت شد.`
    } else if (result.apply === 'failed') {
      tone = 'warning'
      title = `پرداخت ${rial(result.amount)} ریال انجام شد، اما هنوز ثبت نشده است.`
      detail = (result.applyError ? result.applyError + ' ' : '') + 'فروشنده از این پرداخت باخبر شد و آن را ثبت می‌کند؛ نیازی به پرداخت دوباره نیست.'
    } else {
      tone = 'pending'
      title = `پرداخت ${rial(result.amount)} ریال تأیید شد؛ ${
        subscription ? 'در حال فعال‌سازی اشتراک' : `در حال ثبت ${wallet ? 'در کیف پول' : 'در صورتحساب'}`
      }…`
    }
  } else if (result.status === 'unknown' || result.status === 'pending') {
    tone = 'pending'
    title = 'نتیجهٔ پرداخت هنوز از بانک نرسیده است.'
    detail =
      attempts >= POLL_LIMIT
        ? 'پرداخت در چند دقیقهٔ آینده پیگیری می‌شود. اگر مبلغ از حساب شما کم شده باشد و پرداخت تأیید نشود، بانک آن را حداکثر تا ۷۲ ساعت برمی‌گرداند؛ دوباره پرداخت نکنید.'
        : 'چند لحظه صبر کنید…'
  } else {
    tone = 'error'
    title = result.status === 'cancelled' ? 'پرداخت لغو شد.' : 'پرداخت انجام نشد.'
    detail =
      (result.message ? result.message + ' ' : '') +
      'اگر مبلغی از حساب شما کم شده است، بانک آن را حداکثر تا ۷۲ ساعت برمی‌گرداند.'
  }

  return (
    <div className={clsx('op-result', `op-result--${tone}`)} role={tone === 'error' ? 'alert' : 'status'} aria-live='polite'>
      <i
        className={clsx(
          'las fs-1',
          tone === 'success' ? 'la-check-circle' : tone === 'error' ? 'la-times-circle' : tone === 'warning' ? 'la-exclamation-circle' : 'la-hourglass-half'
        )}
        aria-hidden='true'
      />
      <div className='flex-grow-1'>
        <div className='fw-bold'>{title}</div>
        {detail && <div className='fs-7 mt-1'>{detail}</div>}
        {result.status === 'paid' && (
          <div className='op-result__facts'>
            {result.referenceNumber && (
              <span>
                شمارهٔ مرجع: <bdi dir='ltr'>{result.referenceNumber}</bdi>
              </span>
            )}
            {result.traceNumber && (
              <span>
                شمارهٔ پیگیری: <bdi dir='ltr'>{result.traceNumber}</bdi>
              </span>
            )}
            {result.maskedCard && (
              <span>
                کارت: <bdi dir='ltr'>{result.maskedCard}</bdi>
              </span>
            )}
            {result.paidAt && <span>زمان: {result.paidAt}</span>}
            {result.gatewayTitle && <span>درگاه: {result.gatewayTitle}</span>}
          </div>
        )}
        {(wallet || subscription) && result.issuedInvoiceHash && (
          <a
            className='btn btn-sm btn-light-success mt-2'
            href={`${invoiceBase}/invoice/${result.issuedInvoiceHash}`}
            target='_blank'
            rel='noreferrer'
          >
            {subscription ? 'مشاهدهٔ صورتحساب اشتراک' : 'مشاهدهٔ صورتحساب شارژ'}
          </a>
        )}
      </div>
      <button type='button' className='btn btn-sm btn-icon btn-light' onClick={close} aria-label='بستن پیام پرداخت'>
        <i className='las la-times' aria-hidden='true' />
      </button>
    </div>
  )
}
